import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Col, Image, Row, Space, Typography } from 'antd';
import { Avatar, Card, message } from 'antd';
import delay from 'delay';
import { useAtomValue } from 'jotai';
import React, { useCallback, useState } from 'react';
import { useAccount } from 'wagmi';

import { Agree, Color, Disagree } from '@/component/VoteResult';
import { isGotNftAtom } from '@/store';

const { Title, Paragraph, Text } = Typography;

const { Meta } = Card;

const TITLE_LEVEL = 1;

function IssueDetail() {
  const { address } = useAccount();
  const isGotNFT = useAtomValue(isGotNftAtom);
  const [messageApi, contextHolder] = message.useMessage();

  const [isVoted, setVoted] = useState(false);

  const onVote = useCallback(async () => {
    if (isGotNFT) {
      const key = 'GotNFT';
      messageApi.open({
        key,
        type: 'loading',
        content: 'Loading...'
      });
      await delay(2100);
      messageApi.open({
        key,
        type: 'success',
        content: 'Finished!',
        duration: 2
      });
      setVoted(true);
    } else {
      messageApi.open({
        type: 'error',
        content: 'Only Admin & Contributor Could Vote'
      });
    }
  }, [isGotNFT]);

  return (
    <>
      {contextHolder}
      <div>
        <div
          style={{
            width: '100%',
            height: '400px',
            backgroundImage: 'url(https://www.greenpeace.org.uk/wp-content/uploads/2022/09/GP1SZPHR_.jpg)',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPositionY: '25%'
          }}
        />
        <Row gutter={20}>
          <Col span={18}>
            <Title level={TITLE_LEVEL}>
              Deforestation in the Amazon worse than ever for September ahead of Brazilian elections
            </Title>

            <Row>
              <Col span={16}>
                <Space>
                  <Avatar
                    size="large"
                    src="https://images.theconversation.com/files/223729/original/file-20180619-126566-1jxjod2.jpg?ixlib=rb-1.1.0&q=45&auto=format&w=1200&h=1200.0&fit=crop"
                  />
                  <Title level={5} style={{ margin: '0' }}>
                    {address?.slice(0, 6)} <span style={{ marginLeft: '30px' }}>Date: 10 July, 2023</span>
                  </Title>
                </Space>
              </Col>
              <Col span={8}></Col>
            </Row>

            <Title level={3}>
              An area almost twice the size of New York City was deforested in September alone - more than any other
              September on record
            </Title>
            <Paragraph>
              Manaus, 7 October, 2022 – An area of the Amazon almost twice the size of New York City was deforested in
              September, according to alerts from the Brazilian Space Research Institute (INPE)’s system DETER. 1,455
              km2 is the largest area ever recorded for the month of September. The data shows an increase of 48%
              compared to the same month last year. 2022 has seen record-breaking numbers of fires and deforestation.
            </Paragraph>
            <Paragraph>
              <Image
                width="100%"
                src="https://media.greenpeace.org/Assets/V2/ChFHUDBTNTAwMDAwMDAwMDAwMhIPVFIxX1dBVEVSTUFSS0VEGiVcVFIxX1dBVEVSTUFSS0VEXDRcY1w0XDhcR1AxU1pQSE0uanBnIgQIARAPQAE-~/27MDHUF26Z9R/CntqAcGc8dBDkbJP/GP1SZPHM.jpg"
              />
            </Paragraph>
            <Paragraph>
              From January to September, 8,590 km² of the Amazon was deforested (11 times the size of NYC). And in the
              past three years, an area larger than Belgium was deforested in the Amazon according to the official
              government database PRODES.
            </Paragraph>
            <Paragraph>
              <Text strong>Cristiane Mazzetti, spokesperson for Greenpeace Brazil, said:</Text>
              <br />
              “In recent years, the Bolsonaro government has shown complete disregard for a safe climate and for the
              Amazon rainforest, Indigenous Peoples and traditional communities. His administration has actively
              promoted an anti-environment, anti-Indigenous and anti-democratic agenda that has resulted in a severe
              increase in carbon emissions and that paints a grave scenario in Brazil. This destructive project cannot
              continue.”
            </Paragraph>
            <Paragraph>
              <Text strong>Paul Morozzo, senior food and forests campaigner at Greenpeace UK, said:</Text>
              <br />
              “Bolsonaro has allowed and in fact encouraged catastrophic levels of deforestation in the Amazon and other
              climate-critical Brazilian forests. His administration has also lobbied the UK and EU to try and block
              crucial legislation that could stop deforestation-linked products entering our markets.
            </Paragraph>
            <Paragraph>
              <Image
                width="100%"
                src="https://media.greenpeace.org/Assets/V2/ChFHUDBTNTAwMDAwMDAwMDAwMhIPVFIxX1dBVEVSTUFSS0VEGiVcVFIxX1dBVEVSTUFSS0VEXDZcY1w4XDFcR1AxU1pQSEUuanBnIgQIARAPQAE-~/27MDHUF26LQR/wgDNoI8uhmeUlGrw/GP1SZPHE.jpg"
              />
            </Paragraph>
            <Paragraph>
              “The Brazilian elections are not just about the future of Brazil, the result will have an impact on all of
              humanity. If we lose the Amazon, we lose the fight against the climate crisis.”
            </Paragraph>
            <Paragraph>
              By emboldening forest destruction, the Brazilian government’s actions have also caused an increase in
              violence in the field against Indigeneous Peoples and environmental defenders. According to a report
              published by NGO CIMI, 176 Indigenous People were murdered in 2021. Brazil is one of the most dangerous
              countries for environmentalists, according to a report by Global Witness.
            </Paragraph>
            <Paragraph>
              Over 17% of the entire Amazon Basin has already been deforested, and this massive destruction is pushing
              the forest closer to a tipping point. If 20 to 25% of the Amazon is lost, it would fail as an ecosystem,
              according to a research by scientists Carlos Nobre and Thomas Lovejoy. The high deforestation rate in
              Brazil has caused the country’s carbon emissions to increase. Some 46% of the country’s emissions come
              from deforestation.
            </Paragraph>
          </Col>
          <Col span={6}>
            <Title
              level={TITLE_LEVEL}
              style={{
                position: 'sticky',
                top: '20px'
              }}
            >
              <Space direction="vertical">
                <Card
                  cover={
                    <img
                      src="https://static.vecteezy.com/system/resources/previews/003/529/020/original/a-hand-places-a-ballot-paper-in-a-ballot-box-election-concept-flat-design-illustration-vector.jpg"
                      alt="vote"
                    />
                  }
                  actions={
                    isVoted
                      ? []
                      : [
                          <CheckCircleOutlined
                            key="check"
                            style={{ fontSize: '25px', color: Color.Agree }}
                            onClick={onVote}
                          />,
                          <CloseCircleOutlined
                            key="close"
                            style={{ fontSize: '25px', color: Color.Disagree }}
                            onClick={onVote}
                          />
                        ]
                  }
                >
                  <Meta
                    title="Make your choice!"
                    description="Do you agree we donate 0.01 Matic to Greenpeace for forest protection ?"
                  />
                </Card>

                {isVoted && <Agree />}
                {isVoted && <Disagree />}
              </Space>
            </Title>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default IssueDetail;
